/**
 * Spec 0045 (U2) — per-tenant GoHighLevel (GHL) integration management
 * (saas-auth SASATH-0053).
 *
 * SUPER_ADMIN-only grouping rendered on the Settings tab in the space the email
 * fields vacated (U1). The caller (TenantSettings) gates rendering on
 * `operator?.isSuperAdmin`; this component additionally degrades to a contained
 * "SUPER_ADMIN required" state if the API returns 403. It self-fetches the
 * tenant's GHL config AFTER mount with isolated loading/empty/error/403 states —
 * it is NOT loaded in tenantWorkspaceLoader, so an ordinary SAAS_ADMIN's Settings
 * tab is never broken by a 403 here (same rationale as 0044).
 *
 * SECRET HYGIENE (load-bearing). The GHL private integration token is a
 * credential the admin already holds and pastes in — NOT a saas-auth-minted
 * once-revealed secret — so it is a `type="password"`, write-only TextField, not
 * `SecretOnceDialog`. The read never returns the full token (only `tokenSet` /
 * `tokenSuffix`); the field is NEVER pre-filled. On an existing integration the
 * field is left blank with an "enter a new token to replace" placeholder, and
 * `privateIntegrationToken` is OMITTED from the PUT body when blank (the backend
 * sets the token unconditionally, so resending blank would clear it). The token
 * is never persisted, logged, or written to storage.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { GhlIntegrationDto } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function TenantGhlIntegration({ tenantId }: { tenantId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  // `undefined` = not yet loaded; `null` = loaded but not configured (404).
  const [config, setConfig] = useState<GhlIntegrationDto | null | undefined>(undefined);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  // Form state. `locationId` is editable text; `token` is the write-only
  // password input (never pre-filled); `active` is the toggle.
  const [locationId, setLocationId] = useState('');
  const [token, setToken] = useState('');
  const [active, setActive] = useState(true);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  // Client-side validation message (e.g. token required on create). Kept
  // separate from ApiError so it isn't collapsed into a generic code string.
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const exists = config != null;

  // Sync the editable form fields from the loaded config. The token field is
  // intentionally reset to blank (never pre-filled from the masked read).
  function syncForm(next: GhlIntegrationDto | null) {
    setLocationId(next?.locationId ?? '');
    setActive(next?.active ?? true);
    setToken('');
  }

  async function load() {
    setLoadError(null);
    try {
      const result = await apiAdmin.ghlIntegration.get(tenantId);
      setConfig(result);
      setForbidden(false);
      syncForm(result);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfig(null);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setConfig(null);
    }
  }

  useEffect(() => {
    // `load()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setValidationError(null);
    const trimmedToken = token.trim();
    // Token is required on create; on an existing integration it is left blank
    // unless replacing.
    if (!exists && trimmedToken.length === 0) {
      setValidationError(t('ghl.errors.tokenRequired', 'A private integration token is required.'));
      return;
    }
    setSaving(true);
    try {
      // Compose the request body, OMITTING privateIntegrationToken when blank so
      // an existing token is never cleared by a no-token save.
      const req: { locationId: string; active: boolean; privateIntegrationToken?: string } = {
        locationId: locationId.trim(),
        active,
      };
      if (trimmedToken.length > 0) {
        req.privateIntegrationToken = trimmedToken;
      }
      const saved = await apiAdmin.ghlIntegration.set(tenantId, req);
      setConfig(saved);
      syncForm(saved);
      snack.success(t('ghl.saved', 'GHL integration saved.'));
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await apiAdmin.ghlIntegration.remove(tenantId);
      snack.success(t('ghl.deleted', 'GHL integration removed.'));
      setConfirmDelete(false);
      setConfig(null);
      syncForm(null);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmDelete(false);
        return;
      }
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
        <Typography variant="h3" component="h3" gutterBottom>
          {t('ghl.title', 'GoHighLevel integration')}
        </Typography>
        <Alert severity="warning">
          {t('ghl.superAdminRequired', 'Super admin required to manage the GHL integration.')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
      <Typography variant="h3" component="h3" gutterBottom>
        {t('ghl.title', 'GoHighLevel integration')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'ghl.explanation',
          "This config lets the tenant's product backend operate against the tenant's GoHighLevel account. The private integration token is write-only — it is never displayed again after you save it.",
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {config === undefined ? (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <CircularProgress size={16} />
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading', 'Loading')}
          </Typography>
        </Stack>
      ) : (
        <>
          {exists ? (
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Chip
                  size="small"
                  label={
                    config.active
                      ? t('ghl.status.active', 'Active')
                      : t('ghl.status.inactive', 'Inactive')
                  }
                  color={config.active ? 'success' : 'default'}
                  variant={config.active ? 'filled' : 'outlined'}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    config.tokenSet
                      ? t('ghl.tokenSet', 'Token set ({{suffix}})', {
                          suffix: config.tokenSuffix ?? '…',
                        })
                      : t('ghl.tokenUnset', 'No token stored')
                  }
                />
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('ghl.locationLabel', 'Location ID')}: {config.locationId || '—'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('ghl.created', 'Created')}: {formatTimestamp(config.createdAt)} ·{' '}
                {t('ghl.updated', 'Updated')}: {formatTimestamp(config.updatedAt)}
              </Typography>
            </Stack>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('ghl.empty', 'No GHL integration configured for this tenant yet.')}
            </Alert>
          )}

          <ErrorBanner message={validationError} error={saveError} />
          <Box component="form" onSubmit={(e) => void handleSave(e)} noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('ghl.fields.locationId', 'Location ID')}
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                  placeholder={t('ghl.fields.locationIdPlaceholder', 'e.g. loc_abc123')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('ghl.fields.token', 'Private integration token')}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoComplete="new-password"
                  required={!exists}
                  placeholder={
                    exists
                      ? t('ghl.fields.tokenReplacePlaceholder', 'Enter a new token to replace')
                      : t('ghl.fields.tokenPlaceholder', 'Paste the GHL private integration token')
                  }
                  helperText={
                    exists
                      ? t(
                          'ghl.fields.tokenReplaceHelper',
                          'Leave blank to keep the current token. Type a value only to replace it.',
                        )
                      : t(
                          'ghl.fields.tokenHelper',
                          'Write-only — shown only as a masked suffix after saving.',
                        )
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch checked={active} onChange={(e) => setActive(e.target.checked)} />
                  }
                  label={t('ghl.fields.active', 'Active')}
                />
              </Grid>
            </Grid>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {exists ? t('common.save', 'Save') : t('ghl.create', 'Create integration')}
              </Button>
              {exists ? (
                <Button color="error" disabled={deleting} onClick={() => setConfirmDelete(true)}>
                  {t('ghl.delete', 'Delete integration')}
                </Button>
              ) : null}
            </Stack>
          </Box>
        </>
      )}
      <ConfirmDialog
        open={confirmDelete}
        title={t('ghl.deleteTitle', 'Delete GHL integration?')}
        body={t(
          'ghl.deleteBody',
          "This removes the tenant's GHL integration. The tenant's product backend will no longer be able to operate against its GoHighLevel account until a new integration is configured. This cannot be undone.",
        )}
        severity="error"
        confirmLabel={t('ghl.delete', 'Delete integration')}
        busy={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Paper>
  );
}
